import { inject, Injectable } from '@angular/core';
import { State, Action, StateContext, Selector } from '@ngxs/store';
import { AddApplication, CheckIsUserHasRegistrationsToExamType, GenerateApplications, GetUserApplications, GetApplicationsByExam, GetExamineeRecords, GetUserRecords, GetGuestRecords, ExportUserRecords } from './application.actions';
import { LoadingState } from '@tsin-core/entities';
import { catchError, tap } from 'rxjs';
import { ApplicationService } from '@tsin-core/services/http/application.service';
import { IExamList } from '@tsin-core/models/exam.model';
import { IApplication, IExamineeRecord, IExamineeShort, IGuestRecord, IUserRecord } from '@tsin-core/models/application.model';
import { saveAs } from 'file-saver';

export class ApplicationStateModel {
  public applications: IApplication[] = [];
  public shortApplications: IExamineeShort[] = [];
  public selectedApplication: IApplication | null = null;
  public examineeRecords: IExamineeRecord[] = [];
  public userRecords: IUserRecord[] = [];
  public guestRecords: IGuestRecord[] = [];
  public loading!: LoadingState;
}

const defaults = {
  applications: [],
  shortApplications: [],
  selectedApplication: null,
  examineeRecords: [],
  userRecords: [],
  guestRecords: [],
  loading: LoadingState.notLoading,
};

@State<ApplicationStateModel>({
  name: 'applicationState',
  defaults
})

@Injectable()
export class ApplicationState {

  applicationService: ApplicationService = inject(ApplicationService);


  @Selector()
  static getLoading(state: ApplicationStateModel): LoadingState {
    return state.loading;
  }

  @Selector()
  static getUserApplications(state: ApplicationStateModel): IApplication[] {
    return state.applications;
  }

  @Selector()
  static getUserSelectedApplication(state: ApplicationStateModel): IApplication | null {
    return state.selectedApplication;
  }

  @Selector()
  static getUserShortApplications(state: ApplicationStateModel): IExamineeShort[] {
    return state.shortApplications;
  }

  @Selector()
  static getExamineeRecords(state: ApplicationStateModel): IExamineeRecord[] {
    return state.examineeRecords;
  }

  @Selector()
  static getUserRecords(state: ApplicationStateModel): IUserRecord[] {
    return state.userRecords;
  }

  @Selector()
  static getGuestRecords(state: ApplicationStateModel): IGuestRecord[] {
    return state.guestRecords;
  }


  @Action(GetUserApplications)
  getUserApplications(ctx: StateContext<ApplicationStateModel>, action: GetUserApplications) {
    const state = ctx.getState();
    ctx.patchState({ loading: LoadingState.loadingList });
    return this.applicationService.getUserApplications(action.userId).pipe(
      tap((response: { data: IApplication[] }) => {
        ctx.patchState({
          applications: response.data,
          loading: LoadingState.notLoading,
        });
      }),
      catchError((error) => {
        ctx.patchState({ loading: LoadingState.notLoading });
        throw error;
      })
    );
  }


  @Action(GenerateApplications)
  generateApplications(ctx: StateContext<ApplicationStateModel>, action: GenerateApplications) {
    const state = ctx.getState();
    ctx.patchState({ loading: LoadingState.loadingAddUpdate });
    return this.applicationService.generateDryRunApplications(action.payload).pipe(
      tap((generatedResponse: any) => {
        const state = ctx.getState();
        console.log('The applications generated are', generatedResponse)
        ctx.patchState({
          loading: LoadingState.notLoading,
        });
      }),
      catchError((error) => {
        ctx.patchState({ loading: LoadingState.notLoading });
        throw error;
      })
    );
  }


  @Action(AddApplication)
  addApplication(ctx: StateContext<ApplicationStateModel>, action: any) {
    const state = ctx.getState();
    ctx.patchState({ loading: LoadingState.loadingAddUpdate });
    return this.applicationService.addExamApplication(action.payload).pipe(
      tap((response: any) => {
        // const applications = [...state.applications, response.data];
        ctx.patchState({
          selectedApplication: response.data,
          loading: LoadingState.notLoading,
        });
      }),
      catchError((error) => {
        ctx.patchState({ loading: LoadingState.notLoading });
        throw error;
      })
    );
  }

  @Action(GetApplicationsByExam)
  getApplicationsByExam(ctx: StateContext<ApplicationStateModel>, action: GetApplicationsByExam) {
    const state = ctx.getState();
    ctx.patchState({ loading: LoadingState.loadingList });
    return this.applicationService.getApplicationsByExam(action.payload).pipe(
      tap((response: { data: IExamineeShort[] }) => {
        console.log('Response Data::>>>', response);
        // const shortApplications = [...state.shortApplications, response.data];
        const shortApplications = response.data;
        ctx.patchState({
          shortApplications,
          loading: LoadingState.notLoading,
        });
      }),
      catchError((error) => {
        ctx.patchState({ loading: LoadingState.notLoading });
        throw error;
      })
    );
  }

  @Action(CheckIsUserHasRegistrationsToExamType)
  checkIsUserHasRegistrationsToExamType(ctx: StateContext<ApplicationStateModel>, action: CheckIsUserHasRegistrationsToExamType) {
    const state = ctx.getState();
    ctx.patchState({ loading: LoadingState.loadingList });
    return this.applicationService.checkIsUserHasRegistrationsToExamType(action.payload).pipe(
      tap((response: any) => {
        ctx.patchState({
          loading: LoadingState.notLoading,
        });
      }),
      catchError((error) => {
        ctx.patchState({ loading: LoadingState.notLoading });
        throw error;
      })
    );
  }

  @Action(GetExamineeRecords)
  getExamineeRecords(ctx: StateContext<ApplicationStateModel>, action: GetExamineeRecords) {
    ctx.patchState({ loading: LoadingState.loadingList });
    return this.applicationService.getApplicationUsers<IExamineeRecord>(this.buildUsersParams('Registered', action.examType)).pipe(
      tap((response) => {
        ctx.patchState({ examineeRecords: response?.data ?? [], loading: LoadingState.notLoading });
      }),
      catchError((error) => {
        ctx.patchState({ loading: LoadingState.notLoading });
        throw error;
      })
    );
  }

  @Action(GetUserRecords)
  getUserRecords(ctx: StateContext<ApplicationStateModel>, action: GetUserRecords) {
    ctx.patchState({ loading: LoadingState.loadingList });
    return this.applicationService.getApplicationUsers<IUserRecord>(this.buildUsersParams('Not Registered', action.examType)).pipe(
      tap((response) => {
        ctx.patchState({ userRecords: response?.data ?? [], loading: LoadingState.notLoading });
      }),
      catchError((error) => {
        ctx.patchState({ loading: LoadingState.notLoading });
        throw error;
      })
    );
  }

  @Action(GetGuestRecords)
  getGuestRecords(ctx: StateContext<ApplicationStateModel>, action: GetGuestRecords) {
    ctx.patchState({ loading: LoadingState.loadingList });
    return this.applicationService.getApplicationUsers<IGuestRecord>(this.buildUsersParams('Guest', action.examType)).pipe(
      tap((response) => {
        ctx.patchState({ guestRecords: response?.data ?? [], loading: LoadingState.notLoading });
      }),
      catchError((error) => {
        ctx.patchState({ loading: LoadingState.notLoading });
        throw error;
      })
    );
  }

  @Action(ExportUserRecords)
  exportUserRecords(ctx: StateContext<ApplicationStateModel>, action: ExportUserRecords) {
    const { status, examType, fileName } = action.payload;
    ctx.patchState({ loading: LoadingState.loadingList });
    return this.applicationService.getApplicationUsersExcel(this.buildUsersParams(status, examType)).pipe(
      tap((blob) => {
        saveAs(blob, fileName);
        ctx.patchState({ loading: LoadingState.notLoading });
      }),
      catchError((error) => {
        ctx.patchState({ loading: LoadingState.notLoading });
        throw error;
      })
    );
  }

  /** Builds the query params for /api/applications/users; examId is intentionally omitted. */
  private buildUsersParams(status: string, examType?: string): Record<string, string> {
    const params: Record<string, string> = { status };
    if (examType) params['examType'] = examType;
    return params;
  }
}
